import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { RippleModule } from 'primeng/ripple';
import { ButtonModule } from 'primeng/button';
import {
  DropdownControlComponent,
  IDropdownSelect,
} from '../form/dropdown-control/dropdown-control.component';
import { Observable, Subject, of, takeUntil } from 'rxjs';
import {
  Collection,
  ManagedFilter,
} from '../../../graphql/generated/generated-types';
import { ActivatedRoute, Router } from '@angular/router';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CommonModule } from '@angular/common';
import { IShopParams, IMultiValueFilterParams } from '../../../routes/shop/shop.component';
import { isEqual } from 'lodash-es';
import { EditableManagedFilter } from '../../../routes/shop/managed-filter.class';



export enum QuickFiltersFields {
  Application = 'Application',
  Category = 'Category',
  Viscosity = 'Viscosity',
  Volume = 'Volume',
  Brand = 'Brand',
  OEMSpec = 'OEMSpec',
  Acea = 'Acea',
}
@Component({
  selector: 'app-quick-filters',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    RippleModule,
    DropdownControlComponent,
    ProgressSpinnerModule,
  ],
  templateUrl: './quick-filters.component.html',
  styleUrl: './quick-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuickFiltersComponent implements OnInit, OnDestroy, OnChanges {
  @Input() collection!: Collection;
  @Input() topFilters: EditableManagedFilter[];
  @Input() totalResults = 0;
  @Input() loading$: Observable<boolean> = of(false)
  @Input() activeQuickFilters: IMultiValueFilterParams;
  @Input() sidebar: boolean = false;

  public filterOptions: { [key: string]: any } = {};
  public filterControls: FormGroup = new FormGroup({});
  public facetsQueryParams$: Observable<any | string[]>;

  private unsubscribeFormControlTrigger$: Subject<void> = new Subject<void>();
  private unsubscribeGlobal$: Subject<void> = new Subject<void>();
  public innerTopFilters: EditableManagedFilter[] = [];

  public previousControlValues!: any;
  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (this.loading$) {
      this.subscribeToLoadingChanges();
    }
  }

  private subscribeToLoadingChanges() {
    this.loading$.pipe(takeUntil(this.unsubscribeGlobal$)).subscribe(loading => {
      if (loading) {
        this.filterControls.disable({ emitEvent: false });
      } else {
        this.filterControls.enable({ emitEvent: false });
      }
    })
  }


  ngOnChanges(changes: SimpleChanges): void {
    if ('topFilters' in changes) {
      this.innerTopFilters = this.topFilters;
      this.resetFormControlSubscription();
      this.generateEntryFilterControls();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeFormControlTrigger$.next();
    this.unsubscribeFormControlTrigger$.complete();
    this.unsubscribeGlobal$.next();
    this.unsubscribeGlobal$.complete();
  }

  private resetFormControlSubscription() {
    this.unsubscribeFormControlTrigger$.next();
  }

  public clearFilters() {
    this.filterControls.reset(null, { emitEvent: false });
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: { quickFilters: null, filters: null, page: null, search: null, lf: null } as IShopParams,
      state: {
        noScroll: true,
      },
    });

  }

  public subscribeToControlChanges() {
    this.filterControls.valueChanges.pipe(takeUntil(this.unsubscribeFormControlTrigger$)).subscribe({
      next: (values) => {
        if (!isEqual(this.previousControlValues, values)) {
          // CONVERT HERE TO NEW OBJECT VALUES : 
          const convertedValues: IMultiValueFilterParams = {};
          for (const key in values) {
            if (values.hasOwnProperty(key)) {
              const numKey = parseInt(key, 10); // Parse key as a number
              if (values[key]) {
                convertedValues[numKey] = [values[key]]; // Wrap the value in an array
              } else {
                convertedValues[numKey] = null as any;
              }
            }
          }



          // TEMP FIX BECAUSE VALUES COMING FROM DROPDOWN WITHOUT MULTI : 
          let quickFilters: IMultiValueFilterParams = { ...this.activeQuickFilters }; // Copy existing activeQuickFilters
          for (const key in convertedValues) {
            if (convertedValues.hasOwnProperty(key)) {
              const value = convertedValues[key];
              const numKey = parseInt(key, 10); // Parse key as a number

              if (value !== null && Array.isArray(value) && value.length > 0) {
                quickFilters[numKey] = value;
              } else {
                delete quickFilters[numKey];
              }
            }
          }


          const queryParamsNew: IShopParams = {
            quickFilters: this.convertToQueryParamString(quickFilters),
            filters: null,
            search: null,
            page: null
          };

          this.updateQueryParams(queryParamsNew);
        }
        this.previousControlValues = values;
      }
    });
  }


  private updateQueryParams(queryParamsNew: IShopParams): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParamsNew as IShopParams,
      queryParamsHandling: 'merge',
      state: {
        noScroll: true,
      },
    });

  }



  // Helper function to convert object to query parameter string
  private convertToQueryParamString(params: IMultiValueFilterParams): string | null {
    // Check if the params object is empty
    if (Object.keys(params).length === 0) {
      return null;
    }
    return Object.entries(params)
      .map(([key, values]) => `${key}:${values.join(';')}`)
      .join(',');
  }



  public generateEntryFilterControls() {
    const formGroupControls: { [key: string]: any } = {};
    if (this.innerTopFilters && this.innerTopFilters?.length > 0) {
      //  const newFilterOptions: { [key: string]: IDropdownSelect[] } = {}; // Create a new object

      for (const filter of this.innerTopFilters) {
        const filterOptions: IDropdownSelect[] = [];
        for (const filterValue of filter.values) {
          filterOptions.push({
            value: filterValue.id,
            label: filterValue.name,
            count: filterValue.count,
            disabled: filterValue.count == 0
          });
        }

        //   newFilterOptions[filter.id] = filterOptions;
        this.filterOptions[filter.id] = filterOptions;
        formGroupControls[filter.id] = [undefined];

      }
      this.filterControls = this.formBuilder.group(formGroupControls);

      for (const filterId in this.filterOptions) {
        if (this.filterOptions.hasOwnProperty(filterId)) {
          const filterOptions = this.filterOptions[filterId];
          for (const option of filterOptions) {
            for (const key in this.activeQuickFilters) {
              for (const child in this.activeQuickFilters[key]) {
                if (this.activeQuickFilters.hasOwnProperty(key) && this.activeQuickFilters[key][child] === option.value) {
                  const control = this.filterControls.get(filterId);
                  if (control) {
                    const currentValue = control.value;
                    if (Array.isArray(currentValue)) {
                      // If it's already an array, append option.value to it
                      const updatedValue = [...currentValue, option.value];
                      control.setValue(updatedValue, { emitEvent: false });
                    } else {
                      // If it's not an array, create a new array with both values
                      // TEMP FOR SINGLE VALUES
                      control.setValue(option.value, { emitEvent: false });
                      //   control.setValue([option.value], { emitEvent: false });
                    }
                  }
                  break;
                }
              }

            }
          }
        }
      }
      this.subscribeToControlChanges();
    }
  }



}
